import { Grid, Stack, Divider, Typography, Box, Tooltip, useMediaQuery } from "@mui/material";
import classNames from "classnames";
import { useMemo, useState, useEffect, useRef } from "react";
import { calcProPercentsBasedOnFilterAndDays, itemPercentage } from "../../../../utilis/common";
import { calculateDiffBetweenPercentages, pickComparisonColor } from "../../../../utilis/percentage-comparison";
import { useSelector } from "react-redux";
import { CUSTOM_TAB_PAGE_TYPE, PAGE_TYPE } from "../../../../types/pages.type";
import ColorBox from "../../../../components/shared/dashboard/ColorBox";
import useLoadingToggle from "../../../../components/hooks/useLoadingToggle";
import styles from "./DynamicDataCardTotalCard.module.scss";

// Utility: Map PAGE_TYPE to styles
const getPageTypeStyle = (type) => {
    const map = {
        [PAGE_TYPE.HOSPITAL]: styles.gradientTextPrimaryHospital,
        [PAGE_TYPE.COMMUNITY_TRANSFER]: styles.gradientTextPrimaryCommunityTransfer,
        [PAGE_TYPE.DECEASED]: styles.gradientTextPrimaryDeceased,
        [PAGE_TYPE.ADMISSION]: styles.gradientTextPrimaryAdmission,
        [PAGE_TYPE.OVERALL]: styles.gradientTextPrimaryOverall,
        [PAGE_TYPE.ADT]: styles.gradientTextPrimaryAdt,
    };
    return map[type] || styles.gradientTextPrimary;
};

const getBorderStyle = (type) => {
    const map = {
        [PAGE_TYPE.HOSPITAL]: styles.borderHospital,
        [PAGE_TYPE.COMMUNITY_TRANSFER]: styles.borderCommunityTransfer,
        [PAGE_TYPE.DECEASED]: styles.borderDeceased,
        [PAGE_TYPE.ADMISSION]: styles.borderAdmission,
        [PAGE_TYPE.OVERALL]: styles.borderOverall,
        [PAGE_TYPE.ADT]: styles.borderAdt,
    };
    return map[type] || null;
};

const SubTotalCardItem = ({
    title,
    dashboard, // Dashboard name for matching (separate from display title)
    total,
    percentage,
    originalTotal,
    isAverageLength = false,
    loading,
    borderColorStyle,
    page,
    handleCardClick,
    isSelectedTotalCard,
    averageCensus,
    filter,
    filterComparison,
    averageCensusComparison,
    projectionDays,
    isComparingAgainstAvgCensus,
    lockedTotalBy,
    transferType,
    isFirstItemInPriorityData,
    isFilterSelected,
    disableInteractions = false,
    uniqueKey,
    selectedUniqueKey,
    isCardSelected = false, // Pass isCardSelected to prevent recalculation when card is selected
    itemComparison = null, // Comparison data for this item
    dataComparison = null, // Full comparison data array
    data = null, // Full data array (for censusAverage access)
    admissionCompareAgainst = null,
    admissionCompareAgainstComparison = null,
    reverseColorsAdmissionPage = false,
    question = null,
    cardFilter = null,
    selectedItem = [],
    loadingItems = {}, // Loading state from parent
    handleToggleWithLoader = null // Loader wrapper function from parent
}) => {
    const isSmallScreen = useMediaQuery('(max-width:1380px)');
    const isNarrowCardColumn = useMediaQuery('(max-width:1280px)');
    // Use dashboard for matching, fallback to title if dashboard not provided (backward compatibility)
    const matchingTitle = dashboard || title;
    const isMatching = matchingTitle === page || (page === PAGE_TYPE.OVERALL && (matchingTitle === CUSTOM_TAB_PAGE_TYPE.OVERALL_INCOMING || matchingTitle === CUSTOM_TAB_PAGE_TYPE.OVERALL_OUTGOING));
    
    // Check if this specific item is selected by checking for composite key in cardFilter
    // For items with duplicate titles, check if composite key (accessor__uniqueKey) is in selectedItems
    // For backward compatibility, also check the old way (selectedUniqueKey match)
    const compositeKey = question?.accessor && uniqueKey ? `${question.accessor}__${uniqueKey}` : null;
    const acc = question?.accessor;
    const cardSelectedItems = [
        ...(Array.isArray(cardFilter?.[acc]) ? cardFilter[acc] : []),
        ...(Array.isArray(selectedItem) ? selectedItem : []),
    ];
    const isSelectedByCompositeKey =
        compositeKey && cardSelectedItems.includes(compositeKey);
    // Once any composite keys exist for this accessor, drive ticks only from the list — not from
    // selectedUniqueKey. Otherwise after unselecting one of several slices, local selectedUniqueKey
    // can still match this row while isSelectedTotalCard stays true (another slice selected), and the
    // tick would wrongly remain.
    const hasCompositeSelectionInStore =
        Boolean(acc) &&
        cardSelectedItems.some(
            (k) => typeof k === "string" && k.startsWith(`${acc}__`)
        );
    const isSelected =
        isSelectedByCompositeKey ||
        (isSelectedTotalCard &&
            isMatching &&
            selectedUniqueKey === uniqueKey &&
            (!acc || !hasCompositeSelectionInStore));

    // Calculate percentage for sub-total item
    // If card is selected, use the original percentage directly without recalculating
    // This prevents percentage from changing when the card's own filter is applied
    const percentageTotal = useMemo(() => {
        // If card is selected and percentage is available, use it directly
        if (isCardSelected && percentage != null && percentage !== '') {
            return percentage;
        }
        // Otherwise, recalculate percentage based on current filter state
        return calcProPercentsBasedOnFilterAndDays(
        percentage,
        { ...filter },
        projectionDays,
        true,
        isComparingAgainstAvgCensus ||
        lockedTotalBy === "census" ||
            (!transferType || transferType.length === 0)
        );
    }, [isCardSelected, percentage, filter, projectionDays, isComparingAgainstAvgCensus, lockedTotalBy, transferType]);

    // Comparison logic for colorBox
    const { comparingAgainstDifferentFacility } = useSelector(state => state.comparison);
    const specialPages = [PAGE_TYPE.ADMISSION];
    const isSpecialPage = specialPages.find((x) => x === page)?.length > 0;

    const comparisonData = useMemo(() => {
        if (!itemComparison || isAverageLength) {
            return {
                comparisonColor: null,
                comparingAgainst: null,
                comparingAgainstScaled: null,
                numberOfDays: null
            };
        }
        // Use originalTotal for comparison calculation (before filter projection)
        // originalTotal comes from value?.total in totalByPage (unfiltered)
        const currentTotal = originalTotal || total;
        // itemComparison comes from cardItemComparison?.totalByPage[key] (comparison data)
        const comparisonTotal = itemComparison?.total || itemComparison?.value || 0;

        // Get censusAverage from data object if available (like DxCardTemplate)
        // For combineTab, data is an array, so check if it's an array first
        // Otherwise, data might be an object with censusAverage property
        // Fallback to props if not found in data
        let currentCensusAverage = averageCensus;
        let comparisonCensusAverage = averageCensusComparison;

        if (Array.isArray(data) && data.length > 0) {
            // For array data (like combineTab), check first item for censusAverage
            if (data[0]?.censusAverage) {
                currentCensusAverage = data[0].censusAverage;
            }
        } else if (data && typeof data === 'object' && data.censusAverage) {
            // For object data, use censusAverage directly
            currentCensusAverage = data.censusAverage;
        }

        if (Array.isArray(dataComparison) && dataComparison.length > 0) {
            // For array comparison data, check first item for censusAverage
            if (dataComparison[0]?.censusAverage) {
                comparisonCensusAverage = dataComparison[0].censusAverage;
            }
        } else if (dataComparison && typeof dataComparison === 'object' && dataComparison.censusAverage) {
            // For object comparison data, use censusAverage directly
            comparisonCensusAverage = dataComparison.censusAverage;
        }

        let comparisonColor;
        let comparingAgainst;
        let comparingAgainstScaled;
        let numberOfDays;

        if (page === PAGE_TYPE.ADMISSION) {
            // For admission page, use admissionCompareAgainst (like DxCardTemplate)
            const percentage = itemPercentage(currentTotal, admissionCompareAgainst, "number", page);
            const percentageComparison = itemPercentage(
                comparisonTotal,
                admissionCompareAgainstComparison,
                "number",
                page
            );
            const {
                percentageDiff,
                itemTotalComparisonScaled,
                numberOfDays: numberOfDaysTemp,
            } = calculateDiffBetweenPercentages(
                percentage,
                percentageComparison,
                reverseColorsAdmissionPage,
                filter,
                filterComparison,
                comparisonTotal,
                projectionDays
            );
            numberOfDays = numberOfDaysTemp;
            comparingAgainstScaled = itemTotalComparisonScaled;
            comparisonColor = pickComparisonColor(percentageDiff, isSpecialPage);
            comparingAgainst = comparisonTotal;
        } else {
            // For non-admission pages, use censusAverage (like DxCardTemplate)
            const percentage = itemPercentage(currentTotal, currentCensusAverage, "number", page);
            const percentageComparison = itemPercentage(
                comparisonTotal,
                comparisonCensusAverage,
                "number",
                page
            );
            const {
                percentageDiff,
                itemTotalComparisonScaled,
                numberOfDays: numberOfDaysTemp,
            } = calculateDiffBetweenPercentages(
                percentage,
                percentageComparison,
                false,
                filter,
                filterComparison,
                comparisonTotal,
                projectionDays
            );
            numberOfDays = numberOfDaysTemp;
            comparingAgainstScaled = itemTotalComparisonScaled;
            comparisonColor = pickComparisonColor(percentageDiff, isSpecialPage);
            comparingAgainst = comparisonTotal;
        }

        if (comparingAgainstDifferentFacility) {
            comparisonColor = null;
        }

        return {
            comparisonColor,
            comparingAgainst,
            comparingAgainstScaled,
            numberOfDays
        };
    }, [itemComparison, isAverageLength, originalTotal, total, data, dataComparison, averageCensus, averageCensusComparison, page, filter, filterComparison, projectionDays, isSpecialPage, comparingAgainstDifferentFacility, admissionCompareAgainst, admissionCompareAgainstComparison, reverseColorsAdmissionPage]);

    const { comparisonColor, comparingAgainst, comparingAgainstScaled, numberOfDays } = comparisonData;
    
    // Check if this specific item is loading
    const isLoading = loadingItems[uniqueKey] || false;

    // Shared click handler so clicking anywhere on the card (including the checkmark)
    // triggers the same filter logic
    const canClick =
        isMatching &&
        !disableInteractions &&
        !isLoading;

    const handleClick = canClick
        ? () => {
            if (handleToggleWithLoader) {
                handleToggleWithLoader(() => {
                    if (handleCardClick) {
                        handleCardClick(uniqueKey);
                    }
                }, uniqueKey);
            } else if (handleCardClick) {
                handleCardClick(uniqueKey);
            }
        }
        : undefined;

    return (
        <Stack
            sx={{
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                borderRadius: "8px",
                cursor: isMatching && !disableInteractions && !isLoading ? "pointer" : "default",
                position: "relative",
                transition: "all 0.3s ease",
                minHeight: { xs: "60px", sm: "65px", md: "70px" },
                padding: isNarrowCardColumn
                    ? { xs: "8px 4px", sm: "8px 4px", md: "10px 4px" }
                    : { xs: "8px 4px", sm: "10px 6px", md: "12px 2px" },
                minWidth: 0,
                ...(isMatching && isNarrowCardColumn ? { paddingTop: "18px" } : {}),
            }}
            className={loading ? "skeleton" : undefined}
        >
            {/* Current Page Badge - Top Right */}
            {isMatching && !isLoading && (
                <div
                    style={{
                        position: "absolute",
                        top: isNarrowCardColumn ? "6px" : "8px",
                        right: isNarrowCardColumn ? "4px" : "8px",
                        background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                        color: "white",
                        padding: isNarrowCardColumn ? "2px 7px" : "4px 12px",
                        borderRadius: "12px",
                        fontSize: isNarrowCardColumn ? "7px" : "9px",
                        fontWeight: "700",
                        zIndex: 2,
                        boxShadow: "0 3px 8px rgba(25, 118, 210, 0.4)",
                        letterSpacing: isNarrowCardColumn ? "0.4px" : "0.8px",
                        transform: isNarrowCardColumn ? "none" : "translateY(-50%)",
                        transition: "all 0.2s ease",
                    }}
                >
                    CURRENT
                </div>
            )}

            
            {/* Selected Checkmark - Left Center */}
            {isSelected && !isLoading && (
                <div
                    onClick={handleClick}
                    style={{
                    position: 'absolute',
                    top: '50%',
                        left: itemComparison && !isAverageLength ? '38px' : '20px', // Move right if ColorBox is present
                    color: '#1976d2',
                    fontSize: '24px',
                    fontWeight: '900',
                    zIndex: 2,
                    transform: 'translateY(-50%)',
                    transition: 'all 0.2s ease',
                        textShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                        cursor: canClick ? 'pointer' : 'default'
                    }}
                >
                    ✓
                </div>
            )}

            <div
                className={classNames(
                    styles.projectionContainer,
                    styles.withSubTotalCard,
                    loading && styles.skeleton,
                    // Apply page-specific border color only when matching
                    isMatching && borderColorStyle,
                    isSelected && styles.selectedCard
                )}
                style={
                    isSelected ? {
                        boxShadow: '0 4px 20px rgba(25, 118, 210, 0.25)',
                        transform: 'scale(1.02) translateY(-2px)',
                        borderWidth: '2px',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                        borderRadius: '8px',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative'
                    } : isMatching ? {
                        // Matching cards use page-specific border color from CSS
                        borderWidth: '1px',
                        backgroundColor: 'rgba(25, 118, 210, 0.02)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                        position: 'relative',
                        overflow: 'visible',
                        borderRadius: '8px',
                        transition: 'all 0.3s ease'
                    } : {
                        // Non-matching cards get grey border
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        backgroundColor: '#fafafa',
                        borderRadius: '8px',
                        transition: 'all 0.3s ease',
                        position: 'relative'
                    }
                }
                onClick={handleClick}
                onMouseEnter={(e) => {
                    if (isMatching && !loading && !isLoading) {
                        e.currentTarget.style.transform = 'scale(1.01) translateY(-0.5px)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(25, 118, 210, 0.2)';
                    }
                }}
                onMouseLeave={(e) => {
                    if (isMatching && !loading && !isLoading) {
                        e.currentTarget.style.transform = isSelected ? 'scale(1.02) translateY(-2px)' : 'scale(1) translateY(0)';
                        e.currentTarget.style.boxShadow = isSelected ? '0 4px 20px rgba(25, 118, 210, 0.25)' : '0 2px 8px rgba(0, 0, 0, 0.05)';
                    }
                }}
            >
                {/* Left indicator: show ColorBox normally, or a three-dot loader when loading */}
                {!isAverageLength && itemComparison && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        {isLoading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '3px',
                                }}
                            >
                                {[0, 1, 2].map((dotIndex) => (
                                    <Box
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={dotIndex}
                                        sx={{
                                            width: 4,
                                            height: 4,
                                            borderRadius: '50%',
                                            backgroundColor: '#1976d2',
                                            opacity: 0.3,
                                            animation: 'dotPulse 0.9s ease-in-out infinite',
                                            animationDelay: `${dotIndex * 0.15}s`,
                                            '@keyframes dotPulse': {
                                                '0%, 100%': { transform: 'scale(1)', opacity: 0.3 },
                                                '50%': { transform: 'scale(1.6)', opacity: 1 },
                                            },
                                        }}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <ColorBox
                                sx={{
                                    transform: 'scale(0.75)',
                                    transformOrigin: 'center'
                                }}
                                color={comparisonColor}
                                comparingAgainst={comparingAgainst}
                                comparingAgainstScaled={comparingAgainstScaled}
                                numberOfDays={numberOfDays}
                            />
                        )}
                    </Box>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        marginBottom: '4px'
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        className={classNames(
                            styles.projectionTitle,
                            isSelected && styles.selectedTitle
                        )}
                        sx={{
                            ...isMatching && !isSelected ? {
                                color: '#1976d2',
                                fontWeight: 600
                            } : {
                                color: '#424242',
                                fontWeight: 500
                            },
                            fontSize: { xs: '11px', sm: '12px', md: '13px' },
                            letterSpacing: '0.3px',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            hyphens: 'auto',
                            lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 },
                            textAlign: 'center'
                        }}
                    >
                        {loading ? (
                            <span className="skeleton-text" style={{ visibility: "hidden" }}>{title}</span>
                        ) : (() => {
                            const rawTitle = title || "";
                            const capitalizedTitle = rawTitle.charAt(0).toUpperCase() + rawTitle.slice(1);
                            const shouldTruncate = isSmallScreen && capitalizedTitle.length > 9;
                            const displayTitle = shouldTruncate
                                ? `${capitalizedTitle.slice(0, 8)}…`
                                : capitalizedTitle;

                            return shouldTruncate ? (
                                <Tooltip title={capitalizedTitle}>
                                    <span>{displayTitle}</span>
                                </Tooltip>
                            ) : (
                                displayTitle
                            );
                        })()}
                    </Typography>


                </Box>
                <Stack alignItems="center" spacing={0.5}>
                    <Typography
                        variant="body2"
                        className={classNames(
                            styles.projectionSubTitle,
                            isSelected && styles.selectedTotal
                        )}
                        sx={{
                            ...isMatching ? {
                                color: '#1976d2',
                                fontWeight: 700,
                                fontSize: { xs: '16px', sm: '18px', md: '20px' }
                            } : {
                                color: '#212121',
                                fontWeight: 600,
                                fontSize: { xs: '14px', sm: '16px', md: '18px' }
                            },
                            lineHeight: 1,
                            textAlign: 'center'
                        }}
                    >
                        {loading ? (
                            <span className="skeleton-text" style={{ visibility: "hidden" }}>{total}</span>
                        ) : (total < 0 ? `-${Math.abs(total)}` : total)}
                    </Typography>
                    {!isAverageLength && percentageTotal != null && percentageTotal !== '' && (
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: { xs: '9px', sm: '10px', md: '11px' },
                                color: isMatching ? '#1976d2' : '#757575',
                                opacity: isMatching ? 1 : 0.8,
                                fontWeight: isMatching ? 500 : 400,
                                textAlign: 'center'
                            }}
                        >
                            {loading ? (
                                <span className="skeleton-text" style={{ visibility: "hidden" }}>(0.0%)</span>
                            ) : `(${percentageTotal}%)`}
                        </Typography>
                    )}                   
                </Stack>
            </div>
        </Stack>
    );
};

const DynamicDataCardTotalCard = ({
    loading = false,
    total = 0,
    averageCensus = 0,
    averageCensusComparison = 0,
    filter = "",
    filterComparison = "",
    projectionDays = 1,
    question,
    data,
    dataComparison,
    subTitleText = "",
    filterTotal = 0,
    page,
    handleCardClick,
    isSelectedTotalCard,
    selectedItem = [],
    priorityNumber,
    lockedTotalBy,
    isComparingAgainstAvgCensus,
    transferType,
    cardFilter,
    isDisabled = false,
    disableInteractions = false,
    isMobile = false,
    showAverageLength = false,
    admissionCompareAgainst = null,
    admissionCompareAgainstComparison = null,
    reverseColorsAdmissionPage = false,
    compactForHeaderPopover = false,
}) => {
    const isNarrowTotalLayout = useMediaQuery("(max-width:1280px)");

    const isFilterSelected = useMemo(() => {
        return cardFilter?.priorityData?.length > 0 || (transferType != null && transferType?.length > 0);
    }, [cardFilter, transferType]);

    const cardItem = useMemo(() => data?.find((item) => item?.id === question?.accessor), [data, question]);
    const cardItemComparison = useMemo(() => dataComparison?.find((item) => item?.id === question?.accessor), [dataComparison, question]);
    const totalValue = useMemo(() => calcProPercentsBasedOnFilterAndDays(cardItem?.total || 0, filter, projectionDays), [cardItem, filter, projectionDays]);

    // Check if this card is selected (in cardFilter.priorityData) - if so, use original percentage without recalculating
    const isCardSelected = useMemo(() => {
        if (!question?.isCustom || !cardFilter?.priorityData || !question?.accessor) return false;
        // Check if question.accessor matches any type in cardFilter.priorityData array
        return cardFilter.priorityData.some(priorityItem => priorityItem?.type === question.accessor);
    }, [question, cardFilter]);
    
    const percentage = useMemo(() => {
        // If card is selected, use the original percentage from cardItem without recalculating
        // This prevents percentage from changing when the card's own filter is applied
        if (isCardSelected && cardItem?.percentage != null) {
            return cardItem.percentage;
        }
        // Otherwise, recalculate percentage based on current filter state
        return calcProPercentsBasedOnFilterAndDays(
        cardItem?.percentage,
        { ...filter },
        projectionDays,
        true,
        isComparingAgainstAvgCensus ||
        lockedTotalBy === "census" ||
            (!transferType || transferType.length === 0)
        );
    }, [isCardSelected, cardItem?.percentage, filter, projectionDays, isComparingAgainstAvgCensus, lockedTotalBy, transferType]);

    const textColorStyle = useMemo(() => getPageTypeStyle(cardItem?.parentCard), [cardItem]);
    const borderColorStyle = useMemo(() => getBorderStyle(cardItem?.parentCard), [cardItem]);

    // Comparison logic for main card ColorBox
    const { comparingAgainstDifferentFacility } = useSelector(state => state.comparison);
    const specialPages = [PAGE_TYPE.ADMISSION];
    const isSpecialPage = specialPages.find((x) => x === page)?.length > 0;

    const mainCardComparisonData = useMemo(() => {
        if (!cardItemComparison || showAverageLength) {
            return {
                comparisonColor: null,
                comparingAgainst: null,
                comparingAgainstScaled: null,
                numberOfDays: null
            };
        }
        
        // Use original total from cardItem (before filter projection) for percentage calculation
        const currentTotal = cardItem?.total || 0;
        // Get comparison total from cardItemComparison (original, before filter projection)
        const comparisonTotal = cardItemComparison?.total || cardItemComparison?.value || 0;
        
        // Calculate filtered/projected comparison value (similar to totalValue)
        const comparisonTotalValue = calcProPercentsBasedOnFilterAndDays(comparisonTotal, filterComparison, projectionDays);

        // Get censusAverage from data object if available
        let currentCensusAverage = averageCensus;
        let comparisonCensusAverage = averageCensusComparison;

        if (Array.isArray(data) && data.length > 0) {
            if (data[0]?.censusAverage) {
                currentCensusAverage = data[0].censusAverage;
            }
        } else if (data && typeof data === 'object' && data.censusAverage) {
            currentCensusAverage = data.censusAverage;
        }

        if (Array.isArray(dataComparison) && dataComparison.length > 0) {
            if (dataComparison[0]?.censusAverage) {
                comparisonCensusAverage = dataComparison[0].censusAverage;
            }
        } else if (dataComparison && typeof dataComparison === 'object' && dataComparison.censusAverage) {
            comparisonCensusAverage = dataComparison.censusAverage;
        }

        let comparisonColor;
        let comparingAgainst;
        let comparingAgainstScaled;
        let numberOfDays;

        if (page === PAGE_TYPE.ADMISSION) {
            // For admission page, use admissionCompareAgainst
            const percentage = itemPercentage(currentTotal, admissionCompareAgainst, "number", page);
            const percentageComparison = itemPercentage(
                comparisonTotal,
                admissionCompareAgainstComparison,
                "number",
                page
            );
            const {
                percentageDiff,
                itemTotalComparisonScaled,
                numberOfDays: numberOfDaysTemp,
            } = calculateDiffBetweenPercentages(
                percentage,
                percentageComparison,
                reverseColorsAdmissionPage,
                filter,
                filterComparison,
                comparisonTotal,
                projectionDays
            );
            numberOfDays = numberOfDaysTemp;
            comparingAgainstScaled = itemTotalComparisonScaled;
            comparisonColor = pickComparisonColor(percentageDiff, isSpecialPage);
            // Use filtered/projected comparison value (similar to totalValue)
            comparingAgainst = comparisonTotalValue;
        } else {
            // For non-admission pages, use censusAverage
            const percentage = itemPercentage(currentTotal, currentCensusAverage, "number", page);
            const percentageComparison = itemPercentage(
                comparisonTotal,
                comparisonCensusAverage,
                "number",
                page
            );
            const {
                percentageDiff,
                itemTotalComparisonScaled,
                numberOfDays: numberOfDaysTemp,
            } = calculateDiffBetweenPercentages(
                percentage,
                percentageComparison,
                false,
                filter,
                filterComparison,
                comparisonTotal,
                projectionDays
            );
            numberOfDays = numberOfDaysTemp;
            comparingAgainstScaled = itemTotalComparisonScaled;
            comparisonColor = pickComparisonColor(percentageDiff, isSpecialPage);
            // Use filtered/projected comparison value (similar to totalValue)
            comparingAgainst = comparisonTotalValue;
        }

        if (comparingAgainstDifferentFacility) {
            comparisonColor = null;
        }

        return {
            comparisonColor,
            comparingAgainst,
            comparingAgainstScaled,
            numberOfDays
        };
    }, [cardItem, cardItemComparison, averageCensus, averageCensusComparison, data, dataComparison, page, admissionCompareAgainst, admissionCompareAgainstComparison, reverseColorsAdmissionPage, filter, filterComparison, projectionDays, isSpecialPage, comparingAgainstDifferentFacility, showAverageLength]);

    // Track which specific item is selected by unique key
    const [selectedUniqueKey, setSelectedUniqueKey] = useState(null);
    // Ref to store the modified cardItem with specific patientIds for duplicate titles
    const modifiedCardItemRef = useRef(null);
    // Loading toggle hook for showing loader on click
    const { loadingItems, handleToggleWithLoader } = useLoadingToggle();

    // Reset selectedUniqueKey when isSelectedTotalCard changes to false or a different value
    useEffect(() => {
        if (!isSelectedTotalCard) {
            setSelectedUniqueKey(null);
            modifiedCardItemRef.current = null;
        }
    }, [isSelectedTotalCard]);

    // Keep local highlight in sync when composite selections update (e.g. multi-select unselect one slice).
    const mergedCompositeKeysForCard = useMemo(() => {
        const acc = question?.accessor;
        if (!acc) return [];
        const fromFilter = Array.isArray(cardFilter?.[acc]) ? cardFilter[acc] : [];
        const fromSelected = Array.isArray(selectedItem) ? selectedItem : [];
        return [...new Set([...fromFilter, ...fromSelected])];
    }, [question?.accessor, cardFilter, selectedItem]);

    useEffect(() => {
        const acc = question?.accessor;
        if (!acc || selectedUniqueKey == null) return;
        const anyComposite = mergedCompositeKeysForCard.some(
            (k) => typeof k === "string" && k.startsWith(`${acc}__`)
        );
        if (!anyComposite) return;
        const expected = `${acc}__${selectedUniqueKey}`;
        if (!mergedCompositeKeysForCard.includes(expected)) {
            setSelectedUniqueKey(null);
        }
    }, [question?.accessor, mergedCompositeKeysForCard, selectedUniqueKey]);

    const subTotalItems = useMemo(() => {
        const comparisonTotalByPage = cardItemComparison?.totalByPage || {};
        return Object.entries(cardItem?.totalByPage || {}).map(([key, value]) => {
            // If card is selected, use original percentage without recalculating
            // This prevents percentage from changing when the card's own filter is applied
            // Check for both null and empty string to ensure we have a valid percentage
            const hasValidPercentage = value?.percentage != null && value?.percentage !== '';
            const percentage = isCardSelected && hasValidPercentage
                ? value.percentage
                : calcProPercentsBasedOnFilterAndDays(value?.percentage || 0, filter, projectionDays);

            // Find comparison item for this key
            const comparisonValue = comparisonTotalByPage[key] || null;

            return {
                key,
                total: calcProPercentsBasedOnFilterAndDays(value?.total || 0, filter, projectionDays),
                originalTotal: value?.total || 0, // Store original total for comparison
                percentage,
                patientIds: value?.patientIds || [], // Store patientIds from totalByPage
                label: value?.label || null, // Store label from totalByPage if available
                dashboard: value?.dashboard || null, // Store dashboard name for matching
                itemComparison: comparisonValue // Store comparison data for this item
            }
        })
    }, [cardItem, cardItemComparison, filter, projectionDays, isCardSelected]);

    // Check if there are duplicate titles (same dashboard name after removing numeric suffix)
    const hasDuplicateTitles = useMemo(() => {
        if (!cardItem?.totalByPage) return false;
        const titles = Object.keys(cardItem.totalByPage).map(key => key.replace(/_\d+$/, ''));
        const uniqueTitles = new Set(titles);
        return titles.length !== uniqueTitles.size;
    }, [cardItem]);

    // Create a wrapper function that tracks the unique key while calling handleCardClick with title
    const handleItemClick = useMemo(() => {
        return (uniqueKey) => {
            // Extract title from uniqueKey (remove the numeric suffix if any)
            const title = uniqueKey.replace(/_\d+$/, '');
            setSelectedUniqueKey(uniqueKey);

            // Special case: If there are duplicate titles, use patientIds from the specific totalByPage entry
            // (kept for backward compatibility), but we ALWAYS pass uniqueKey so handleToggle can
            // look up patientIds from totalByPage for every dashboard slice (hospital_0, admission_3, etc.)
            if (hasDuplicateTitles && cardItem && data && data.length > 0) {
                // Find the specific item in subTotalItems by uniqueKey
                const specificItem = subTotalItems.find(item => item.key === uniqueKey);
                if (specificItem && specificItem.patientIds && specificItem.patientIds.length > 0) {
                    // Store in ref (kept for backward compatibility, although not used now)
                    modifiedCardItemRef.current = {
                        ...cardItem,
                        patientIds: specificItem.patientIds
                    };
                } else {
                    // Clear the ref if no specific patientIds found
                    modifiedCardItemRef.current = null;
                }
            } else {
                // Clear the ref if no duplicates
                modifiedCardItemRef.current = null;
            }

            // Always pass uniqueKey so DynamicCardItem / handleToggle can
            // build a composite key (accessor__uniqueKey) and use per‑dashboard patientIds
            if (handleCardClick) {
                handleCardClick(title, uniqueKey);
            }
        };
    }, [handleCardClick, hasDuplicateTitles, cardItem, subTotalItems, data]);

    // For average length display (exhaustInsuranceData)
    const averageLengthItems = useMemo(() => {
        if (!showAverageLength || !cardItem) return [];
        const avgLength = cardItem.averageLength || 0;
        const originalAvgLength = cardItem.originalAverageLength || 0;
        return [
            {
                key: "Average Length Of Stay",
                total: Math.round(avgLength * 10) / 10, // Round to 1 decimal
                originalTotal: Math.round(originalAvgLength * 10) / 10,
                isAverageLength: true
            }
        ];
    }, [showAverageLength, cardItem]);

    if (isDisabled) {
        return null;
    }

    if (compactForHeaderPopover) {
        const itemsCountCompact = showAverageLength ? averageLengthItems.length : subTotalItems.length;
        const hasMoreThanThreeCompact = itemsCountCompact > 3;
        return (
            <Box
                sx={{
                    width: "100%",
                    maxHeight: 260,
                    overflowY: hasMoreThanThreeCompact ? "auto" : "visible",
                    py: 0.5,
                    px: 0.5,
                    boxSizing: "border-box",
                }}
            >
                {showAverageLength
                    ? averageLengthItems.map(({ key, total, originalTotal, isAverageLength }) => {
                        const uniqueKey = key;
                        const title = key.replace(/_\d+$/, "");
                        return (
                            <SubTotalCardItem
                                disableInteractions={disableInteractions}
                                key={uniqueKey}
                                uniqueKey={uniqueKey}
                                selectedUniqueKey={selectedUniqueKey}
                                title={title}
                                total={total}
                                originalTotal={originalTotal}
                                isAverageLength={isAverageLength}
                                page={page}
                                loading={loading}
                                borderColorStyle={borderColorStyle}
                                handleCardClick={handleItemClick}
                                isSelectedTotalCard={isSelectedTotalCard}
                                averageCensus={averageCensus}
                                averageCensusComparison={averageCensusComparison}
                                filter={filter}
                                filterComparison={filterComparison}
                                projectionDays={projectionDays}
                                isComparingAgainstAvgCensus={isComparingAgainstAvgCensus}
                                lockedTotalBy={lockedTotalBy}
                                transferType={transferType}
                                isFilterSelected={isFilterSelected}
                                itemComparison={null}
                                dataComparison={dataComparison}
                                data={data}
                                admissionCompareAgainst={admissionCompareAgainst}
                                admissionCompareAgainstComparison={admissionCompareAgainstComparison}
                                reverseColorsAdmissionPage={reverseColorsAdmissionPage}
                                question={question}
                                cardFilter={cardFilter}
                                selectedItem={selectedItem}
                                loadingItems={loadingItems}
                                handleToggleWithLoader={handleToggleWithLoader}
                            />
                        );
                    })
                    : subTotalItems.map(({ key, total, originalTotal, percentage, label, dashboard, itemComparison }) => {
                        const uniqueKey = key;
                        const displayTitle = label && label.trim() ? label : key.replace(/_\d+$/, "");
                        const dashboardName = dashboard || key.replace(/_\d+$/, "");
                        return (
                            <SubTotalCardItem
                                disableInteractions={disableInteractions}
                                key={uniqueKey}
                                uniqueKey={uniqueKey}
                                selectedUniqueKey={selectedUniqueKey}
                                title={displayTitle}
                                dashboard={dashboardName}
                                total={total}
                                originalTotal={originalTotal}
                                percentage={percentage}
                                page={page}
                                loading={loading}
                                borderColorStyle={borderColorStyle}
                                handleCardClick={handleItemClick}
                                isSelectedTotalCard={isSelectedTotalCard}
                                averageCensus={averageCensus}
                                averageCensusComparison={averageCensusComparison}
                                filter={filter}
                                filterComparison={filterComparison}
                                projectionDays={projectionDays}
                                isComparingAgainstAvgCensus={isComparingAgainstAvgCensus}
                                lockedTotalBy={lockedTotalBy}
                                transferType={transferType}
                                isFilterSelected={isFilterSelected}
                                isCardSelected={isCardSelected}
                                itemComparison={itemComparison}
                                dataComparison={dataComparison}
                                data={data}
                                admissionCompareAgainst={admissionCompareAgainst}
                                admissionCompareAgainstComparison={admissionCompareAgainstComparison}
                                reverseColorsAdmissionPage={reverseColorsAdmissionPage}
                                question={question}
                                cardFilter={cardFilter}
                                selectedItem={selectedItem}
                                loadingItems={loadingItems}
                                handleToggleWithLoader={handleToggleWithLoader}
                            />
                        );
                    })}
            </Box>
        );
    }

    const itemsCount = showAverageLength ? averageLengthItems.length : subTotalItems.length;
    const hasMoreThanThreeItems = itemsCount > 3;
    
    return (
        <div 
            className={classNames("df aic p-t-20 p-b-25", styles.totalOverall, styles.withSubTotalCardMain, isMobile && 'p-b-0 p-t-0')}
            style={{
                background: 'linear-gradient(to bottom, #ffffff 0%, #fafafa 100%)',
                borderRadius: '12px',
                padding: isMobile ? '12px' : isNarrowTotalLayout ? '16px' : '24px',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                ...(isMobile ? {
                    height: '300px'
                } : {
                    height: '21.6rem',
                    minHeight: '21.6rem'
                })
            }}
        >
            <Grid
                container
                spacing={isNarrowTotalLayout ? { xs: 1, sm: 1 } : { xs: 1, sm: 2, md: 3 }}
                sx={{ flexWrap: "nowrap", height: "100%", width: "100%", minWidth: 0 }}
            >
                {/* Total Section */}
                <Grid item xs={5.5} sm={6} md={6} sx={{ minWidth: 0, maxWidth: "100%" }}>
                    <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", width: "100%", minWidth: 0 }}>
                        {isMobile ? (
                            <Box
                                className={classNames("ffmar-bold", "fw700", "fs49", styles.withSubTotalCard)}
                                sx={{
                                    fontSize: { xs: '36px', sm: '42px', md: '48px' },
                                    letterSpacing: '-1px',
                                    marginBottom: '8px',                                
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                {/* ColorBox for main card - positioned before the number */}
                                {!showAverageLength && cardItemComparison && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginRight: '4px'
                                        }}
                                    >
                                        <ColorBox
                                            sx={{
                                                transform: 'scale(0.75)',
                                                transformOrigin: 'center'
                                            }}
                                            color={mainCardComparisonData.comparisonColor}
                                            comparingAgainst={mainCardComparisonData.comparingAgainst}
                                            comparingAgainstScaled={mainCardComparisonData.comparingAgainstScaled}
                                            numberOfDays={mainCardComparisonData.numberOfDays}
                                        />
                                    </Box>
                                )}
                                <div>
                                    <div
                                        className={classNames(loading && styles.skeleton, textColorStyle)}
                                    >
                                        {loading ? <span className="skeleton-text">0</span> : (totalValue < 0 ? `-${Math.abs(totalValue)}` : totalValue)}
                                    </div>
                                </div>
                            </Box>
                        ) : (
                            <Box
                                className={classNames("ffmar-bold", "df", "aic", "fw700", "fs49", styles.withSubTotalCard, loading && styles.skeleton, textColorStyle)}
                                sx={{
                                    fontSize: { xs: '36px', sm: '42px', md: '48px' },
                                    letterSpacing: '-1px',
                                    marginBottom: '8px',                                
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                {/* ColorBox for main card - positioned before the number */}
                                {!showAverageLength && cardItemComparison && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginRight: '4px'
                                        }}
                                    >
                                        <ColorBox
                                            sx={{
                                                transform: 'scale(0.75)',
                                                transformOrigin: 'center'
                                            }}
                                            color={mainCardComparisonData.comparisonColor}
                                            comparingAgainst={mainCardComparisonData.comparingAgainst}
                                            comparingAgainstScaled={mainCardComparisonData.comparingAgainstScaled}
                                            numberOfDays={mainCardComparisonData.numberOfDays}
                                        />
                                    </Box>
                                )}
                                {loading ? <span className="skeleton-text">0</span> : (totalValue < 0 ? `-${Math.abs(totalValue)}` : totalValue)}
                            </Box>
                        )}
                        {percentage && !isFilterSelected && (
                            <Box className={classNames(styles.textNumberContainer, styles.textNumberSubContainer)}>
                                {isMobile ? (
                                    <div>
                                        <div
                                            className={classNames(
                                                textColorStyle,
                                                loading && styles.skeleton,
                                                loading && "skeleton-text"
                                            )}
                                            style={{
                                                fontWeight: 600,
                                                marginBottom: '4px',
                                                fontSize: '14px'
                                            }}
                                        >{`(${loading
                                            ? 0
                                            : calcProPercentsBasedOnFilterAndDays(
                                                percentage,
                                                filter,
                                                projectionDays,
                                                filterTotal,
                                                averageCensus
                                            )
                                            }%)`}</div>
                                    </div>
                                ) : (
                                    <Box
                                        className={classNames(
                                            textColorStyle,
                                            loading && styles.skeleton,
                                            loading && "skeleton-text"
                                        )}
                                        sx={{
                                            fontSize: { xs: '14px', sm: '15px', md: '16px' },
                                            fontWeight: 600,
                                            marginBottom: '4px'
                                        }}
                                    >{`(${loading
                                        ? 0
                                        : calcProPercentsBasedOnFilterAndDays(
                                            percentage,
                                            filter,
                                            projectionDays,
                                            filterTotal,
                                            averageCensus
                                        )
                                        }%)`}</Box>
                                )}
                                <Box
                                    className={classNames(styles.gradientTextSubtitle, loading && "skeleton-text")}
                                    sx={{
                                        fontSize: { xs: '11px', sm: '12px', md: '13px' },
                                        opacity: 0.8,
                                        fontWeight: 500
                                    }}
                                >
                                    {loading ? (    
                                        <span className="skeleton-text" style={{ visibility: "hidden" }}>
                                            {subTitleText}
                                        </span>
                                    ) : (
                                        subTitleText
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </Grid>

                <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                        mx: isNarrowTotalLayout ? 0.5 : { xs: 0.5, sm: 1, md: 2 },
                        flexShrink: 0,
                        borderColor: 'rgba(0, 0, 0, 0.06)',
                        borderWidth: '1.5px'
                    }}
                />

                {/* Sub Total Section or Average Length Section */}
                <Grid item xs={6} sm={5} md={5} sx={{ 
                    display: "flex",
                    alignItems: hasMoreThanThreeItems ? "flex-start" : "center",
                    height: "100%",
                    alignSelf: "stretch",
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "visible",
                    position: "relative"
                }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: hasMoreThanThreeItems ? "flex-start" : "center",
                            alignItems: "stretch",
                            width: "100%",
                            minWidth: 0,
                            height: "100%",
                            maxHeight: "100%",
                            paddingTop: { xs: 0.5, sm: 1 },
                            paddingBottom: hasMoreThanThreeItems ? { xs: 2, sm: 2.5 } : { xs: 0.5, sm: 1 },
                            gap: { xs: 1, sm: 1.5 },
                            boxSizing: "border-box",
                            ...(hasMoreThanThreeItems ? {
                                overflowY: "auto",
                                overflowX: "hidden",
                                paddingRight: { xs: "4px", sm: "8px" },
                                paddingLeft: { xs: "2px", sm: "4px" },
                                // Custom scrollbar styling
                                '&::-webkit-scrollbar': {
                                    width: '10px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: 'linear-gradient(to bottom, #f5f5f5 0%, #fafafa 100%)',
                                    borderRadius: '10px',
                                    border: '1px solid #e8e8e8',
                                    boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.03)',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: 'linear-gradient(to bottom, #c1c1c1 0%, #a8a8a8 100%)',
                                    borderRadius: '10px',
                                    border: '1px solid #999',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                    minHeight: '40px',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        background: 'linear-gradient(to bottom, #a8a8a8 0%, #909090 100%)',
                                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                                    },
                                    '&:active': {
                                        background: 'linear-gradient(to bottom, #909090 0%, #787878 100%)',
                                    },
                                },
                                // Firefox scrollbar styling
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#c1c1c1 #f5f5f5',
                            } : {})
                        }}
                    >
                        {showAverageLength ? (
                            // Show average length for exhaustInsuranceData
                            averageLengthItems.map(({ key, total, originalTotal, isAverageLength }) => {
                                const uniqueKey = key; // Use original key as unique identifier
                                const title = key.replace(/_\d+$/, ''); // Clean title for display
                                return (
                                    <SubTotalCardItem
                                        disableInteractions={disableInteractions}
                                        key={uniqueKey}
                                        uniqueKey={uniqueKey}
                                        selectedUniqueKey={selectedUniqueKey}
                                        title={title}
                                        total={total}
                                        originalTotal={originalTotal}
                                        isAverageLength={isAverageLength}
                                        page={page}
                                        loading={loading}
                                        borderColorStyle={borderColorStyle}
                                        handleCardClick={handleItemClick}
                                        isSelectedTotalCard={isSelectedTotalCard}
                                        averageCensus={averageCensus}
                                        averageCensusComparison={averageCensusComparison}
                                        filter={filter}
                                        filterComparison={filterComparison}
                                        projectionDays={projectionDays}
                                        isComparingAgainstAvgCensus={isComparingAgainstAvgCensus}
                                        lockedTotalBy={lockedTotalBy}
                                        transferType={transferType}
                                        isFilterSelected={isFilterSelected}
                                        itemComparison={null}
                                        dataComparison={dataComparison}
                                        data={data}
                                        admissionCompareAgainst={admissionCompareAgainst}
                                        admissionCompareAgainstComparison={admissionCompareAgainstComparison}
                                        reverseColorsAdmissionPage={reverseColorsAdmissionPage}
                                        question={question}
                                        cardFilter={cardFilter}
                                        selectedItem={selectedItem}
                                        loadingItems={loadingItems}
                                        handleToggleWithLoader={handleToggleWithLoader}
                                    />
                                );
                            })
                        ) : (
                            // Show subTotalItems for other cards
                            subTotalItems.map(({ key, total, originalTotal, percentage, label, dashboard, itemComparison }) => {
                                const uniqueKey = key; // Use original key as unique identifier
                                // Use label if available, otherwise use cleaned title from key
                                const displayTitle = label && label.trim() ? label : key.replace(/_\d+$/, '');
                                // Get dashboard name for matching (fallback to cleaned key if not available)
                                const dashboardName = dashboard || key.replace(/_\d+$/, '');
                                return (
                                    <SubTotalCardItem
                                        disableInteractions={disableInteractions}
                                        key={uniqueKey}
                                        uniqueKey={uniqueKey}
                                        selectedUniqueKey={selectedUniqueKey}
                                        title={displayTitle}
                                        dashboard={dashboardName}
                                        total={total}
                                        originalTotal={originalTotal}
                                        percentage={percentage}
                                        page={page}
                                        loading={loading}
                                        borderColorStyle={borderColorStyle}
                                        handleCardClick={handleItemClick}
                                        isSelectedTotalCard={isSelectedTotalCard}
                                        averageCensus={averageCensus}
                                        averageCensusComparison={averageCensusComparison}
                                        filter={filter}
                                        filterComparison={filterComparison}
                                        projectionDays={projectionDays}
                                        isComparingAgainstAvgCensus={isComparingAgainstAvgCensus}
                                        lockedTotalBy={lockedTotalBy}
                                        transferType={transferType}
                                        isFilterSelected={isFilterSelected}
                                        isCardSelected={isCardSelected}
                                        itemComparison={itemComparison}
                                        dataComparison={dataComparison}
                                        data={data}
                                        admissionCompareAgainst={admissionCompareAgainst}
                                        admissionCompareAgainstComparison={admissionCompareAgainstComparison}
                                        reverseColorsAdmissionPage={reverseColorsAdmissionPage}
                                        question={question}
                                        cardFilter={cardFilter}
                                        selectedItem={selectedItem}
                                        loadingItems={loadingItems}
                                        handleToggleWithLoader={handleToggleWithLoader}
                                    />
                                );
                            })
                        )}
                    </Box>
                </Grid>
            </Grid>
        </div>
    );
};

export default DynamicDataCardTotalCard;
